<template>
  <el-dialog
    title="抽题规则设置"
    :visible.sync="dialogVisible"
    width="400px"
    custom-class="practice-config-dialog"
    append-to-body
    center
    @close="handleClose"
  >
    <div class="practice-config-content" v-loading="loading">
      <div class="config-item">
        <el-checkbox v-model="config.enableSingle" :disabled="maxConfig.singleChoiceCount === 0">单选题</el-checkbox>
        <div class="input-wrapper">
          <el-input-number 
            v-model="config.singleChoiceCount" 
            :min="1" 
            :max="maxConfig.singleChoiceCount"
            :disabled="!config.enableSingle"
            size="small"
            controls-position="right"
          ></el-input-number>
          <span class="unit">道 <span class="max-info">(共{{ maxConfig.singleChoiceCount }}题)</span></span>
        </div>
      </div>
      <div class="config-item">
        <el-checkbox v-model="config.enableMultiple" :disabled="maxConfig.multipleChoiceCount === 0">多选题</el-checkbox>
        <div class="input-wrapper">
          <el-input-number 
            v-model="config.multipleChoiceCount" 
            :min="1" 
            :max="maxConfig.multipleChoiceCount"
            :disabled="!config.enableMultiple"
            size="small"
            controls-position="right"
          ></el-input-number>
          <span class="unit">道 <span class="max-info">(共{{ maxConfig.multipleChoiceCount }}题)</span></span>
        </div>
      </div>
      <div class="config-item">
        <el-checkbox v-model="config.enableTrueFalse" :disabled="maxConfig.trueFalseCount === 0">判断题</el-checkbox>
        <div class="input-wrapper">
          <el-input-number 
            v-model="config.trueFalseCount" 
            :min="1" 
            :max="maxConfig.trueFalseCount"
            :disabled="!config.enableTrueFalse"
            size="small"
            controls-position="right"
          ></el-input-number>
          <span class="unit">道 <span class="max-info">(共{{ maxConfig.trueFalseCount }}题)</span></span>
        </div>
      </div>
      <div class="config-item">
        <el-checkbox v-model="config.enableEssay" :disabled="maxConfig.essayCount === 0">问答题</el-checkbox>
        <div class="input-wrapper">
          <el-input-number 
            v-model="config.essayCount" 
            :min="1" 
            :max="maxConfig.essayCount"
            :disabled="!config.enableEssay"
            size="small"
            controls-position="right"
          ></el-input-number>
          <span class="unit">道 <span class="max-info">(共{{ maxConfig.essayCount }}题)</span></span>
        </div>
      </div>

      <div class="total-count">
        共抽<span class="count-number">{{ totalCount }}</span>道题
      </div>

      <el-button type="primary" class="start-practice-btn" @click="handleStart" :loading="loading">开始练习</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getWrongQuestionCount, getWrongQuestionPracticeConfig } from '@/api/examApi'

export default {
  name: 'WrongQuestionPracticeConfigDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      maxConfig: {
        singleChoiceCount: 0,
        multipleChoiceCount: 0,
        trueFalseCount: 0,
        essayCount: 0
      },
      config: {
        enableSingle: false,
        singleChoiceCount: 0,
        enableMultiple: false,
        multipleChoiceCount: 0,
        enableTrueFalse: false,
        trueFalseCount: 0,
        enableEssay: false,
        essayCount: 0
      }
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    totalCount() {
      let total = 0
      if (this.config.enableSingle) total += Number(this.config.singleChoiceCount || 0)
      if (this.config.enableMultiple) total += Number(this.config.multipleChoiceCount || 0)
      if (this.config.enableTrueFalse) total += Number(this.config.trueFalseCount || 0)
      if (this.config.enableEssay) total += Number(this.config.essayCount || 0)
      return total
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.fetchData()
      }
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const [countRes, configRes] = await Promise.all([
          getWrongQuestionCount(),
          getWrongQuestionPracticeConfig()
        ])
        
        if (countRes && countRes.data) {
          const data = countRes.data
          this.maxConfig = {
            singleChoiceCount: data.singleChoiceCount || 0,
            multipleChoiceCount: data.multipleChoiceCount || 0,
            trueFalseCount: data.trueFalseCount || 0,
            essayCount: data.essayCount || 0
          }
        }

        if (configRes && configRes.data) {
          const data = configRes.data
          this.config = {
            enableSingle: data.singleChoiceCount > 0,
            singleChoiceCount: Math.min(data.singleChoiceCount || 0, this.maxConfig.singleChoiceCount),
            enableMultiple: data.multipleChoiceCount > 0,
            multipleChoiceCount: Math.min(data.multipleChoiceCount || 0, this.maxConfig.multipleChoiceCount),
            enableTrueFalse: data.trueFalseCount > 0,
            trueFalseCount: Math.min(data.trueFalseCount || 0, this.maxConfig.trueFalseCount),
            enableEssay: data.essayCount > 0,
            essayCount: Math.min(data.essayCount || 0, this.maxConfig.essayCount)
          }
        }
      } catch (error) {
        console.error('Failed to fetch config:', error)
        this.$message.error('获取配置失败')
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
    },
    handleStart() {
      this.$emit('start', this.config)
    }
  }
}
</script>

<style scoped lang="scss">
/* 抽题配置弹窗 */
.practice-config-dialog ::v-deep .el-dialog {
  border-radius: 12px;
}

.practice-config-dialog ::v-deep .el-dialog__header {
  padding: 20px 0;
  text-align: center;
}

.practice-config-dialog ::v-deep .el-dialog__title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.practice-config-content {
  padding: 0 20px 20px;
}

.config-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.config-item .el-checkbox {
  margin-right: 0;
  font-size: 16px;
  color: #333;
}

.input-wrapper {
  display: flex;
  align-items: center;
}

.input-wrapper .unit {
  margin-left: 8px;
  color: #666;
}

.max-info {
  font-size: 12px;
  color: #999;
}

.total-count {
  text-align: center;
  margin: 30px 0;
  font-size: 16px;
  color: #666;
}

.count-number {
  color: #5c4cf8;
  font-weight: bold;
  font-size: 18px;
  margin: 0 4px;
}

.start-practice-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
  background-color: #3e2ae6;
  border-color: #3e2ae6;
}

.start-practice-btn:hover {
  background-color: #5c4cf8;
  border-color: #5c4cf8;
}
</style>
